import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { ContactPanel } from "components/ContactPanel";
import { TaskPanel } from "components/TaskPanel";
import { CondoSwitcher } from "components/CondoSwitcher";
import { BoardMembersList } from "components/BoardMembersList";
import { ResidentsList } from "components/ResidentsList";
import { WorkersList } from "components/WorkersList";
import { ProvidersList } from "components/ProvidersList";
import { PendingContactsList } from "components/PendingContactsList";
import { ManagersList } from "components/ManagersList";
import { OtherContactsList } from "components/OtherContactsList";
import { TasksList } from "components/TasksList";
import { GlobalContactSearch } from "components/GlobalContactSearch";
import { OnboardingChecklistWidget } from "components/OnboardingChecklistWidget";
import { OnboardingChecklistProvider } from "context/OnboardingChecklistContext";
import { ManagerInstructionsSection } from "components/ManagerInstructionsSection";
import { ManagerInstructionsPanel } from "components/ManagerInstructionsPanel";
import { useCondos } from "context/CondosContext";
import { useBoardMembers } from "context/BoardMembersContext";
import { useResidents } from "context/ResidentsContext";
import { useWorkers } from "context/WorkersContext";
import { useProviders } from "context/ProvidersContext";
import { usePendingContacts } from "context/PendingContactsContext";
import { useManagers } from "context/ManagersContext";
import { useTasks } from "context/TasksContext";
import { useAuth } from "context/AuthContext";
import { useOtherContacts } from "context/OtherContactsContext";
import { useAssistantIdentity } from "hooks/useAssistantIdentity";
import { shouldShowOthersSection } from "utils/othersSection";
import {
  MOBILE_HOME_DETAIL_HEADER_MENU_PL,
  mobileHomeFloatingDetailHeaderPaddingTop,
} from "constants/pageLayout";
import { resolveAccessibleCondoId } from "utils/accessibleCondo";
import { subscribeContactChatSocket } from "utils/contactChatSocket";
import { ManagerIncomingBanner } from "components/ManagerIncomingBanner";
import {
  playManagerAlertSound,
  showSystemNotification,
  startTitleAlert,
  stopTitleAlert,
  tabIsBackground,
  unlockManagerAlerts,
} from "utils/managerMessageAlert";

type HomeProps = {
  registerOpenHomeContactList?: (openList: (() => void) | null) => void;
};

export default function Home(props: HomeProps = {}) {
  const { registerOpenHomeContactList } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  );
  const [openedFromBoard, setOpenedFromBoard] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedManagerInstructions, setSelectedManagerInstructions] =
    useState(false);
  const [incomingCount, setIncomingCount] = useState(0);
  const [incomingPreview, setIncomingPreview] = useState("");
  const [mobileChatListOpen, setMobileChatListOpen] = useState(true);
  const selectedContactIdRef = useRef<number | null>(null);
  selectedContactIdRef.current = selectedContactId;
  const prevSelectionRef = useRef<{
    c: number | null;
    t: number | null;
    i: boolean;
  }>({
    c: null,
    t: null,
    i: false,
  });
  /** Only auto-open the manager chat once on first load — never after condo/contact changes. */
  const managerDefaultAppliedRef = useRef(false);

  useEffect(() => {
    registerOpenHomeContactList?.(() => setMobileChatListOpen(true));
    return () => registerOpenHomeContactList?.(null);
  }, [registerOpenHomeContactList]);

  const {
    selectedCondoId,
    setSelectedCondoId,
    condos,
    loading: condosLoading,
    refresh: refreshCondosList,
  } = useCondos();
  const { managerId, hasWappConnectedNumber, lang, isImpersonating, hasPhone } =
    useAuth();
  const locale = lang === "es" ? "es" : "en";
  const {
    hasConnectedNumber: hasConnectedPropertyManagerWapp,
    assistantName,
    wappAssistantMode,
  } = useAssistantIdentity();

  // Get data from contexts to find the selected contact
  const { residents, refresh: refreshResidents } = useResidents();
  const { boardMembers, refresh: refreshBoardMembers } = useBoardMembers();
  const { workers, otherCondoWorkers, refresh: refreshWorkers } = useWorkers();
  const {
    providers,
    otherCondoProviders,
    refresh: refreshProviders,
  } = useProviders();
  const { pendingContacts, refresh: refreshPendingContacts } =
    usePendingContacts();
  const { managers } = useManagers();
  const { otherContacts, refresh: refreshOtherContacts } = useOtherContacts();
  const { tasks, refresh: refreshTasks } = useTasks();
  const showOthersSection = shouldShowOthersSection(
    hasWappConnectedNumber || hasConnectedPropertyManagerWapp,
    wappAssistantMode,
  );

  const refreshCondoScopedData = useCallback(
    async (condoId: number) => {
      await Promise.all([
        refreshCondosList(),
        refreshResidents(condoId),
        refreshBoardMembers(condoId),
        refreshWorkers(condoId),
        refreshProviders(condoId),
        refreshPendingContacts(condoId),
        refreshTasks(condoId),
        refreshOtherContacts(),
      ]);
    },
    [
      refreshCondosList,
      refreshResidents,
      refreshBoardMembers,
      refreshWorkers,
      refreshProviders,
      refreshPendingContacts,
      refreshTasks,
      refreshOtherContacts,
    ],
  );

  const selectedCondoIdRef = useRef(selectedCondoId);
  selectedCondoIdRef.current = selectedCondoId;
  const knownCondoIdsRef = useRef<Set<number> | null>(null);
  const entityRefreshTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!condosLoading && knownCondoIdsRef.current === null) {
      knownCondoIdsRef.current = new Set(condos.map((c) => c.id));
    }
  }, [condos, condosLoading]);

  const refreshEntityLists = useCallback(
    (entity: "condos" | "residents" | "workers" | "providers" | "board_members") => {
      if (entityRefreshTimerRef.current) {
        window.clearTimeout(entityRefreshTimerRef.current);
      }
      entityRefreshTimerRef.current = window.setTimeout(() => {
        void (async () => {
          if (entity === "condos") {
            const previousIds = knownCondoIdsRef.current ?? new Set<number>();
            const nextCondos = await refreshCondosList();
            const nextIds = new Set(nextCondos.map((c) => c.id));
            knownCondoIdsRef.current = nextIds;

            const newlyCreated = nextCondos.filter((c) => !previousIds.has(c.id));
            if (newlyCreated.length > 0) {
              const selected = newlyCreated.reduce((best, condo) =>
                condo.id > best.id ? condo : best
              );
              setSelectedCondoId(selected.id);
            }
            return;
          }

          const condoId = selectedCondoIdRef.current;
          if (condoId == null) {
            return;
          }
          if (entity === "residents") {
            await refreshResidents(condoId);
          } else if (entity === "board_members") {
            await refreshBoardMembers(condoId);
          } else if (entity === "workers") {
            await refreshWorkers(condoId);
          } else if (entity === "providers") {
            await refreshProviders(condoId);
          }
          await refreshPendingContacts(condoId);
        })();
      }, 300);
    },
    [
      refreshCondosList,
      refreshResidents,
      refreshBoardMembers,
      refreshWorkers,
      refreshProviders,
      refreshPendingContacts,
      setSelectedCondoId,
    ]
  );

  const managerContactId = managers.find((manager) => manager.id === managerId)
    ?.contact.id;

  useEffect(() => {
    if (managerContactId == null) {
      return;
    }
    return subscribeContactChatSocket(managerContactId, (event) => {
      let raw: { type?: string; entity?: string };
      try {
        raw = JSON.parse(event.data);
      } catch {
        return;
      }
      if (raw?.type !== "entities_changed" || !raw.entity) {
        return;
      }
      const entity = raw.entity;
      if (
        entity === "condos" ||
        entity === "residents" ||
        entity === "board_members" ||
        entity === "workers" ||
        entity === "providers"
      ) {
        refreshEntityLists(entity);
      }
    });
  }, [managerContactId, refreshEntityLists]);

  useEffect(() => {
    if (hasPhone) {
      return;
    }
    const unlock = () => unlockManagerAlerts();
    window.addEventListener("pointerdown", unlock);
    return () => window.removeEventListener("pointerdown", unlock);
  }, [hasPhone]);

  useEffect(() => {
    if (hasPhone || managerContactId == null) {
      return;
    }
    return subscribeContactChatSocket(managerContactId, (event) => {
      let raw: { type?: string; content?: string | null; channel?: string };
      try {
        raw = JSON.parse(event.data);
      } catch {
        return;
      }
      if (raw?.type !== "assistant" || raw.channel !== "panel") {
        return;
      }
      const lookingAtOwnChat =
        selectedContactIdRef.current === managerContactId && !tabIsBackground();
      if (lookingAtOwnChat) {
        return;
      }
      const preview = (raw.content || "").trim().slice(0, 180);
      setIncomingPreview(preview);
      setIncomingCount((count) => {
        const next = count + 1;
        startTitleAlert(locale, next);
        return next;
      });
      playManagerAlertSound();
      showSystemNotification(
        locale === "es" ? "Eloria te escribió" : "Eloria messaged you",
        preview ||
          (locale === "es"
            ? "Hay un mensaje nuevo en el panel."
            : "There is a new message in the panel."),
        () => {
          setSelectedContactId(managerContactId);
          setMobileChatListOpen(false);
          setIncomingCount(0);
          stopTitleAlert();
        }
      );
    });
  }, [hasPhone, managerContactId, locale]);

  useEffect(() => {
    const clear = () => {
      if (document.visibilityState === "visible" && document.hasFocus()) {
        if (selectedContactIdRef.current === managerContactId) {
          setIncomingCount(0);
          stopTitleAlert();
        }
      }
    };
    window.addEventListener("focus", clear);
    document.addEventListener("visibilitychange", clear);
    return () => {
      window.removeEventListener("focus", clear);
      document.removeEventListener("visibilitychange", clear);
    };
  }, [managerContactId]);

  useEffect(() => {
    return () => {
      if (entityRefreshTimerRef.current) {
        window.clearTimeout(entityRefreshTimerRef.current);
      }
    };
  }, []);

  // Find the selected contact entity across all entity types
  const selectedResident = selectedContactId
    ? residents.find((r) => r.contact.id === selectedContactId) || null
    : null;
  const selectedBoardMember = selectedContactId
    ? boardMembers.find((member) => member.contact.id === selectedContactId) ||
      null
    : null;
  const selectedWorker = selectedContactId
    ? workers.find((w) => w.contact.id === selectedContactId) ||
      otherCondoWorkers.find((w) => w.contact.id === selectedContactId) ||
      null
    : null;
  const selectedProvider = selectedContactId
    ? providers.find((p) => p.contact.id === selectedContactId) ||
      otherCondoProviders.find((p) => p.contact.id === selectedContactId) ||
      null
    : null;
  const selectedPendingContact = selectedContactId
    ? pendingContacts.find((p) => p.contact.id === selectedContactId) || null
    : null;
  const selectedManager = selectedContactId
    ? managers.find((m) => m.contact.id === selectedContactId) || null
    : null;
  const selectedOtherContact = selectedContactId
    ? otherContacts.find((o) => o.contact.id === selectedContactId) || null
    : null;

  const selectedContact = selectedResident?.contact ||
    selectedBoardMember?.contact ||
    selectedWorker?.contact ||
    selectedProvider?.contact ||
    selectedPendingContact?.contact ||
    selectedManager?.contact ||
    selectedOtherContact?.contact ||
    null;

  // Get lastReadAt from the selected entity
  const selectedLastReadAt = selectedResident?.lastReadAt ||
    selectedBoardMember?.lastReadAt ||
    selectedWorker?.lastReadAt ||
    selectedProvider?.lastReadAt ||
    selectedPendingContact?.lastReadAt ||
    selectedOtherContact?.lastReadAt ||
    null;

  // Find the selected task
  const selectedTask = selectedTaskId
    ? tasks.find((t) => t.id === selectedTaskId) || null
    : null;

  const showManagerInstructions =
    selectedManagerInstructions && isImpersonating;

  const listSelectedContactId = showManagerInstructions ? null : selectedContactId;
  const listSelectedTaskId = showManagerInstructions ? null : selectedTaskId;

  const hasConversationDetail = Boolean(
    selectedContact || selectedTask || showManagerInstructions
  );

  useEffect(() => {
    if (!isImpersonating) {
      setSelectedManagerInstructions(false);
    }
  }, [isImpersonating]);

  useEffect(() => {
    if (!hasConversationDetail) {
      setMobileChatListOpen(true);
    }
  }, [hasConversationDetail]);

  useEffect(() => {
    const c = selectedContactId;
    const t = selectedTaskId;
    const i = selectedManagerInstructions;
    const prev = prevSelectionRef.current;
    const had = prev.c !== null || prev.t !== null || prev.i;
    const has = c !== null || t !== null || i;
    if (has && (!had || prev.c !== c || prev.t !== t || prev.i !== i)) {
      setMobileChatListOpen(false);
    }
    prevSelectionRef.current = { c, t, i };
  }, [selectedContactId, selectedTaskId, selectedManagerInstructions]);

  const previousContactIdRef = useRef<number | null>(null);
  const messageIdFromUrlRef = useRef<string | null>(searchParams.get("message-id"));
  const [urlHydrated, setUrlHydrated] = useState(false);

  useEffect(() => {
    messageIdFromUrlRef.current = searchParams.get("message-id");
  }, [searchParams]);

  // Remove activation param from URL when present
  useEffect(() => {
    if (searchParams.has("activation")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("activation");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Drop a selected condo that is outside the loaded portfolio (stale URL / race).
  useEffect(() => {
    if (!urlHydrated || condosLoading || selectedCondoId == null) {
      return;
    }
    if (!condos.some((condo) => condo.id === selectedCondoId)) {
      setSelectedCondoId(null);
    }
  }, [
    urlHydrated,
    condos,
    condosLoading,
    selectedCondoId,
    setSelectedCondoId,
  ]);

  // Apply URL deep links after the portfolio is known. Re-run when searchParams
  // change so later navigations still work; never adopt another admin's condo.
  // While condos are loading, skip URL sync so we do not wipe the landing query.
  useEffect(() => {
    if (condosLoading) {
      return;
    }

    const condoIdFromUrl = searchParams.get("condo");
    const contactIdFromUrl = searchParams.get("contact");
    const taskIdFromUrl = searchParams.get("task");
    const instructionsFromUrl = searchParams.get("instructions");

    const accessibleCondoId = resolveAccessibleCondoId(condoIdFromUrl, condos);
    if (condoIdFromUrl) {
      if (accessibleCondoId != null) {
        setSelectedCondoId(accessibleCondoId);
      } else {
        // Foreign / unknown condo probe: clear selection and strip the probe
        // from the URL (state may already be null, so sync alone might not run).
        setSelectedCondoId(null);
        setSelectedContactId(null);
        setSelectedTaskId(null);
        previousContactIdRef.current = null;
        setUrlHydrated(true);
        const cleaned = new URLSearchParams(searchParams);
        cleaned.delete("condo");
        cleaned.delete("contact");
        cleaned.delete("task");
        setSearchParams(cleaned, { replace: true });
        return;
      }
    }

    if (instructionsFromUrl === "1" && isImpersonating) {
      setSelectedManagerInstructions(true);
      setSelectedContactId(null);
      setSelectedTaskId(null);
      previousContactIdRef.current = null;
      setUrlHydrated(true);
      return;
    }

    if (contactIdFromUrl) {
      const contactId = parseInt(contactIdFromUrl);
      if (!Number.isNaN(contactId)) {
        setSelectedContactId(contactId);
        previousContactIdRef.current = contactId;
      }
    }

    if (taskIdFromUrl) {
      const taskId = parseInt(taskIdFromUrl);
      if (!Number.isNaN(taskId)) {
        setSelectedTaskId(taskId);
      }
    }

    setUrlHydrated(true);
  }, [
    searchParams,
    setSelectedCondoId,
    setSearchParams,
    isImpersonating,
    condos,
    condosLoading,
  ]);

  // On first login/open, default to the current manager conversation (once only).
  useEffect(() => {
    if (!urlHydrated || managerDefaultAppliedRef.current) {
      return;
    }
    const hasContactInUrl = Boolean(searchParams.get("contact"));
    const hasInstructionsInUrl = searchParams.get("instructions") === "1";
    if (
      selectedContactId ||
      selectedTaskId ||
      selectedManagerInstructions ||
      hasContactInUrl ||
      hasInstructionsInUrl ||
      !managerId
    ) {
      return;
    }

    const currentManager = managers.find((manager) => manager.id === managerId);
    if (!currentManager) {
      if (managers.length > 0) {
        managerDefaultAppliedRef.current = true;
      }
      return;
    }

    setSelectedContactId(currentManager.contact.id);
    previousContactIdRef.current = currentManager.contact.id;
    managerDefaultAppliedRef.current = true;
  }, [
    urlHydrated,
    managers,
    managerId,
    selectedContactId,
    selectedTaskId,
    selectedManagerInstructions,
    searchParams,
  ]);

  // Sync selection state -> URL. Do not depend on searchParams: when a deep link
  // arrives, hydrate updates state first; syncing in that same turn with stale
  // null selection would wipe the owned condo from the URL.
  useEffect(() => {
    if (!urlHydrated) {
      return;
    }

    const params = new URLSearchParams();

    if (selectedCondoId) {
      params.set("condo", selectedCondoId.toString());
    }

    if (showManagerInstructions) {
      params.set("instructions", "1");
    } else {
      if (selectedContactId) {
        params.set("contact", selectedContactId.toString());

        const contactChanged = previousContactIdRef.current !== selectedContactId;

        if (contactChanged) {
          previousContactIdRef.current = selectedContactId;
        } else {
          const messageId = messageIdFromUrlRef.current;
          if (messageId) {
            params.set("message-id", messageId);
          }
        }
      }

      if (selectedTaskId) {
        params.set("task", selectedTaskId.toString());
      }
    }

    setSearchParams(params, { replace: true });
  }, [
    urlHydrated,
    selectedCondoId,
    selectedContactId,
    selectedTaskId,
    showManagerInstructions,
    setSearchParams,
  ]);

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1">
      <ManagerIncomingBanner
        count={incomingCount}
        preview={incomingPreview}
        locale={locale}
        onOpen={() => {
          if (managerContactId != null) {
            setSelectedContactId(managerContactId);
            setMobileChatListOpen(false);
          }
          setIncomingCount(0);
          stopTitleAlert();
        }}
        onDismiss={() => {
          setIncomingCount(0);
          stopTitleAlert();
        }}
      />
      {hasConversationDetail && mobileChatListOpen && (
        <button
          type="button"
          className="absolute inset-0 z-[25] bg-black/40 lg:hidden"
          aria-label={locale === "es" ? "Cerrar lista" : "Close list"}
          onClick={() => setMobileChatListOpen(false)}
        />
      )}

      <div
        className={`absolute inset-y-0 left-0 z-30 flex w-[min(100%,20rem)] max-w-full flex-col overflow-hidden border-r border-gray-200 bg-white shadow-xl transition-transform duration-300 ease-in-out lg:static lg:z-auto lg:w-72 lg:max-w-none lg:translate-x-0 lg:shadow-none ${
          mobileChatListOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-1 flex-col overflow-y-auto overscroll-y-contain px-4 pb-4 pt-14 lg:px-3 lg:py-3">
          <OnboardingChecklistProvider condoId={selectedCondoId}>
            <OnboardingChecklistWidget
              scope="account"
              onEntitiesMaybeChanged={refreshEntityLists}
              onOpenEloriaChat={(contactId) => {
                setSelectedTaskId(null);
                setSelectedManagerInstructions(false);
                setMobileChatListOpen(false);
                setSelectedContactId((current) => {
                  if (current === contactId) {
                    // Remount chat so messages refetch after guidance lands.
                    queueMicrotask(() => setSelectedContactId(contactId));
                    return null;
                  }
                  return contactId;
                });
              }}
            />
            <GlobalContactSearch
              onSelectContact={(condoId, contactId) => {
                setSelectedCondoId(condoId);
                setSelectedContactId(contactId);
                setSelectedTaskId(null);
                setSelectedManagerInstructions(false);
                setMobileChatListOpen(false);
              }}
            />
            <CondoSwitcher
              onSelectCondo={(condoId) => {
                if (condoId === selectedCondoId) {
                  void refreshCondoScopedData(condoId);
                  return;
                }
                setSelectedCondoId(condoId);
                setSelectedContactId(null);
                setSelectedTaskId(null);
                setSelectedManagerInstructions(false);
                setMobileChatListOpen(true);
              }}
            />
            <OnboardingChecklistWidget
              key={selectedCondoId ?? "no-condo"}
              scope="condo"
              condoId={selectedCondoId}
              enabled={selectedCondoId != null}
              onEntitiesMaybeChanged={refreshEntityLists}
              onOpenEloriaChat={(contactId) => {
                setSelectedTaskId(null);
                setSelectedManagerInstructions(false);
                setMobileChatListOpen(false);
                setSelectedContactId((current) => {
                  if (current === contactId) {
                    // Remount chat so messages refetch after guidance lands.
                    queueMicrotask(() => setSelectedContactId(contactId));
                    return null;
                  }
                  return contactId;
                });
              }}
            />
            <ManagerInstructionsSection
              selected={selectedManagerInstructions}
              onSelect={() => {
                setSelectedManagerInstructions(true);
                setSelectedContactId(null);
                setSelectedTaskId(null);
                setMobileChatListOpen(false);
              }}
            />
          </OnboardingChecklistProvider>
          <ManagersList
            onSelectContact={(contactId) => {
              setOpenedFromBoard(false);
              setSelectedContactId(contactId);
              setSelectedTaskId(null);
              setSelectedManagerInstructions(false);
              setMobileChatListOpen(false);
            }}
            selectedContactId={listSelectedContactId}
          />
          {selectedCondoId && (
            <>
              <PendingContactsList
                onSelectContact={(contactId) => {
                  setOpenedFromBoard(false);
                  setSelectedContactId(contactId);
                  setSelectedTaskId(null);
                  setSelectedManagerInstructions(false);
                  setMobileChatListOpen(false);
                }}
                selectedContactId={listSelectedContactId}
                condoId={selectedCondoId}
              />
              <BoardMembersList
                onSelectContact={(contactId) => {
                  setOpenedFromBoard(true);
                  setSelectedContactId(contactId);
                  setSelectedTaskId(null);
                  setSelectedManagerInstructions(false);
                  setMobileChatListOpen(false);
                }}
                selectedContactId={listSelectedContactId}
                condoId={selectedCondoId}
              />
              <ResidentsList
                onSelectContact={(contactId) => {
                  setOpenedFromBoard(false);
                  setSelectedContactId(contactId);
                  setSelectedTaskId(null);
                  setSelectedManagerInstructions(false);
                  setMobileChatListOpen(false);
                }}
                selectedContactId={listSelectedContactId}
                condoId={selectedCondoId}
              />
              <WorkersList
                onSelectContact={(contactId) => {
                  setOpenedFromBoard(false);
                  setSelectedContactId(contactId);
                  setSelectedTaskId(null);
                  setSelectedManagerInstructions(false);
                  setMobileChatListOpen(false);
                }}
                selectedContactId={listSelectedContactId}
                condoId={selectedCondoId}
              />
              <ProvidersList
                onSelectContact={(contactId) => {
                  setOpenedFromBoard(false);
                  setSelectedContactId(contactId);
                  setSelectedTaskId(null);
                  setSelectedManagerInstructions(false);
                  setMobileChatListOpen(false);
                }}
                selectedContactId={listSelectedContactId}
                condoId={selectedCondoId}
              />
              {showOthersSection && (
                <OtherContactsList
                  onSelectContact={(contactId) => {
                    setOpenedFromBoard(false);
                    setSelectedContactId(contactId);
                    setSelectedTaskId(null);
                    setSelectedManagerInstructions(false);
                    setMobileChatListOpen(false);
                  }}
                  selectedContactId={listSelectedContactId}
                />
              )}
              <TasksList
                onSelectTask={(taskId) => {
                  setSelectedTaskId(taskId);
                  setSelectedContactId(null);
                  setSelectedManagerInstructions(false);
                  setMobileChatListOpen(false);
                }}
                selectedTaskId={listSelectedTaskId}
                condoId={selectedCondoId}
              />
            </>
          )}
        </div>
      </div>

      {selectedContact && !showManagerInstructions && (
        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${
            mobileChatListOpen ? "hidden lg:flex" : "flex"
          }`}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              className={`shrink-0 border-b border-gray-200 bg-gray-50 pb-2.5 lg:hidden ${MOBILE_HOME_DETAIL_HEADER_MENU_PL}`}
              style={{
                paddingTop: mobileHomeFloatingDetailHeaderPaddingTop(),
              }}
            >
              <div className="flex h-11 shrink-0 items-center pr-3">
                <button
                  type="button"
                  onClick={() => setMobileChatListOpen(true)}
                  className="flex min-h-0 min-w-0 flex-1 touch-manipulation items-center gap-2 text-left text-gray-900 active:bg-gray-200/50"
                  aria-label={
                    locale === "es"
                      ? "Ver lista de conversaciones"
                      : "Show conversation list"
                  }
                >
                  <span className="flex shrink-0 items-center justify-center rounded-full p-1.5 text-gray-700 hover:bg-gray-200/80">
                    <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
                    {selectedContact.name?.trim() ||
                      selectedContact.phone ||
                      selectedContact.mail ||
                      (locale === "es" ? "Conversación" : "Conversation")}
                  </span>
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
              <ContactPanel
                contactId={selectedContact.id}
                name={selectedContact.name || ""}
                phone={selectedContact.phone || ""}
                landlinePhone={selectedContact.landlinePhone || ""}
                mail={selectedContact.mail || ""}
                wappPhoneInvalid={selectedContact.wappPhoneInvalid}
                messageId={searchParams.get("message-id")}
                lastReadAt={selectedLastReadAt}
                residentId={selectedResident?.id}
                boardMemberId={
                  openedFromBoard ? selectedBoardMember?.id : undefined
                }
                isResidentActive={selectedResident?.isActive ?? true}
                workerId={selectedWorker?.id}
                isWorkerActive={selectedWorker?.isActive ?? true}
                isWorkerOnCurrentCondo={
                  selectedWorker
                    ? workers.some((w) => w.id === selectedWorker.id)
                    : undefined
                }
                providerId={selectedProvider?.id}
                pendingContactId={selectedPendingContact?.id}
                isProviderOnCurrentCondo={
                  selectedProvider
                    ? providers.some((p) => p.id === selectedProvider.id)
                    : undefined
                }
                condoId={selectedCondoId ?? undefined}
                onEntitiesMaybeChanged={refreshEntityLists}
              />
            </div>
          </div>
        </div>
      )}

      {selectedTask && !showManagerInstructions && (
        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${
            mobileChatListOpen ? "hidden lg:flex" : "flex"
          }`}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              className={`shrink-0 border-b border-gray-200 bg-gray-50 pb-2.5 lg:hidden ${MOBILE_HOME_DETAIL_HEADER_MENU_PL}`}
              style={{
                paddingTop: mobileHomeFloatingDetailHeaderPaddingTop(),
              }}
            >
              <div className="flex h-11 shrink-0 items-center pr-3">
                <button
                  type="button"
                  onClick={() => setMobileChatListOpen(true)}
                  className="flex min-h-0 min-w-0 flex-1 touch-manipulation items-center gap-2 text-left text-gray-900 active:bg-gray-200/50"
                  aria-label={
                    locale === "es"
                      ? "Ver lista de tareas"
                      : "Show task list"
                  }
                >
                  <span className="flex shrink-0 items-center justify-center rounded-full p-1.5 text-gray-700 hover:bg-gray-200/80">
                    <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
                    {selectedTask.title}
                  </span>
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
              <TaskPanel
                taskId={selectedTask.id}
                title={selectedTask.title}
                crontab={selectedTask.crontab}
              />
            </div>
          </div>
        </div>
      )}

      {showManagerInstructions && (
        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${
            mobileChatListOpen ? "hidden lg:flex" : "flex"
          }`}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              className={`shrink-0 border-b border-gray-200 bg-gray-50 pb-2.5 lg:hidden ${MOBILE_HOME_DETAIL_HEADER_MENU_PL}`}
              style={{
                paddingTop: mobileHomeFloatingDetailHeaderPaddingTop(),
              }}
            >
              <div className="flex h-11 shrink-0 items-center pr-3">
                <button
                  type="button"
                  onClick={() => setMobileChatListOpen(true)}
                  className="flex min-h-0 min-w-0 flex-1 touch-manipulation items-center gap-2 text-left text-gray-900 active:bg-gray-200/50"
                  aria-label={
                    locale === "es"
                      ? "Ver lista de conversaciones"
                      : "Show conversation list"
                  }
                >
                  <span className="flex shrink-0 items-center justify-center rounded-full p-1.5 text-gray-700 hover:bg-gray-200/80">
                    <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
                    {locale === "es"
                      ? `Instrucciones a ${assistantName}`
                      : `Instructions to ${assistantName}`}
                  </span>
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
              <ManagerInstructionsPanel />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
